import React, {useEffect, useState} from 'react'

import {useAuthClient} from '../utils/use-auth-client'
import {useAsync} from '../utils/use-async'
import {ADD_ON} from '../utils/addons-data'
import {Entitlements} from '../components/add-on-components/entitlements'
import {AddOns} from '../components/add-on-components/addons'
import SkeletonLoader from '../components/skeleton-loader'
import {CartAddons} from '../components/cart-addons'
import useDebounce from '../utils/useDebounce'
import {McConfirmModal} from '../components/mc-confirm-modal'
import {OneTime} from '../components/add-on-components/one-time'
import {NewOrderLayout} from '../components/new-order-layout'
import {useSearchParams} from 'react-router-dom'
import {useLocation} from 'react-router'
import {AccountInfo} from '../components/account-info'
import {toast} from 'react-toastify'

import {CURRENCYS, ADDONS_TYPES, BILLING_PREFERENCES} from '../utils/constants'
import {formatPrice} from '../utils/currency-format'
import {useUserFeatures} from '../context/user-features-context'

const isRequiredFlagEnabled = (userFeatures, requiredFlags = []) => {
  if (!requiredFlags.length) return true
  return requiredFlags.every(flag => userFeatures[flag] || false)
}

const HIPAA_COMPLIANT_INDUSTRIES = [
  '52k10f2ml8m1f41u', // dentists
  'pcqjcmfqjvknm31t', // health & wellness
  'e8cgl2m7jp221sx7', // healthcare providers
]

const WEBSITE_ADDON_CODES = [
  'kv1x1fpgv24m',
  'kvtsoc832kz6',
  'kvmt5jfd32b7',
  'kvtt6yf7o5p7',
  'kv1upcfi0yrr',
]

const NO_WEBSITE_ENT_PLANS = [
  'sync-4-dexyp-m2m',
  'plus-4-dexyp-m2m',
  'addon-4-mc-m2m',
]

const MC_PLAN_CODES = [
  ...ADD_ON.add_ons['Marketing Center Plus'].addOnCodes,
  ...ADD_ON.add_ons['Marketing Center Pro'].addOnCodes,
  ...ADD_ON.add_ons['Thryv Signature + Expanded Listings'].addOnCodes,
]
const BC_4_PLAN_CODES = [
  'plus-4-dexyp-m2m',
  'professional-4-dexyp-m2m',
  'unlimited-4-dexyp-m2m',
]
const BC_6_PLAN_CODES = ['bc-6-thryv-m2m']
const BC_SYNC_PLAN_CODES = ['sync-4-dexyp-m2m']
const LISTING_REFRESH_CODES = [
  'addon-4-listrefrsh-m2m',
  'addon-5-listrefrsh-m2m',
]
const STRATEGIC_CONTENT_CREATION_CODE = 'addon-4-stratcontent-m2m'
const THRYV_SUCCESS_SERVICES_CODES = [
  ...LISTING_REFRESH_CODES,
  STRATEGIC_CONTENT_CREATION_CODE,
]

const TL_CODES = [
  'addon-4-tl-tax-m2m',
  'addon-4-tl-m2m',
  'addon-4-tl-mc-m2m',
  'addon-4-tl-semi',
  'addon-4-tl-mc-semi',
]

const WIN_BACK_4_COUPONS = {
  'plus-4-dexyp-m2m': 'pluswinback2022',
  'unlimited-4-dexyp-m2m': 'unlimitedwinback2022',
  'professional-4-dexyp-m2m': 'professionalwinback2022',
}

function getDisableApps(productList) {
  const disableApps = Array.from(
    new Set(
      productList.reduce((acc, item) => {
        if (item?.disableApps) {
          return [...acc, ...item.disableApps]
        }
        return acc
      }, []),
    ),
  )
  return disableApps
}

const getItemCode = (item, addOnCode) => {
  return !addOnCode?.includes('-m2m') && item?.semiAnnually
    ? item?.semiAnnually[0]?.code
    : item?.monthly[0]?.code
}

const getProduct = ({
  item,
  selectedPlan,
  currency,
  quantity,
  currentAddOns,
}) => {
  const availablePlan =
    item[selectedPlan] ?? item['semiAnnually'] ?? item['monthly']
  const productPayment = availablePlan[0]
  if (!productPayment) return

  const catalogPrice =
    productPayment.currencies.find(x => x.currency === currency)?.unitAmount ||
    0
  const existingAddon = currentAddOns?.find(
    ({addOnCode}) => addOnCode === productPayment.code,
  )
  const productPrice = existingAddon?.unitAmount ?? catalogPrice

  let price = Number(
    (quantity ? productPrice * quantity : productPrice).toFixed(2),
  )
  let product = {
    id: productPayment.id,
    product: item.name,
    type: ADDONS_TYPES['ADD_ON'],
    price: `$${price
      .toFixed(2)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
    billingPreference:
      selectedPlan === BILLING_PREFERENCES[0]
        ? BILLING_PREFERENCES[0]
        : BILLING_PREFERENCES[1],
    planCode: availablePlan[0].code,
    disableApps: item.disableApps || [],
  }
  if (item.name === 'Additional Video Views') {
    product.billingPreference = BILLING_PREFERENCES[0]
  }
  if (quantity) {
    product.quantity = quantity
  }
  return product
}

function removeDuplicatesFromListOfProducts(listOfProducts) {
  const uniqueProducts = listOfProducts.reduce((acc, product) => {
    if (!acc.some(p => p.planCode === product.planCode)) {
      acc.push(product)
    }
    return acc
  }, [])
  return uniqueProducts
}

const UpgradeAddonsSelection = ({
  prevStep,
  nextStep,
  selectedPlan,
  billingPreference,
  selectedCountry,
  selectedCountryAbbreviation,
  industryCategoryType,
  selectedIndustry,
  accountInfo,
  accountSubscriptionData,
  isWebsiteIncluded,
  currentGmailItems,
  all5PlanPrices,
  isTSS,
  isEaidFound,
  cohortEaid,
  setIsEaidFound,
  setCohortEaid,
  setAllowInvoice,
  setIsCohort,
  captureOrder4NewUpgrade,
  orderData4CU,
  order5NUId,
  isCancelOrder5,
  cancelOrder5,
  isNewTLMCAccessible,
  isBillingNoticeDisplayed,
  setIsBillingNoticeDisplayed,
}) => {
  const [showRefreshModal, setShowRefreshModal] = useState(false)
  const {pathname} = useLocation()
  const userFeatures = useUserFeatures()
  const isUpgrade = pathname === '/order/upgrade'
  const [listOfProducts, setListOfProducts] = useState(
    selectedPlan?.industryType === 'thryv' &&
      accountInfo?.planCode?.includes('sync') &&
      !accountInfo?.hasPaidOnboardingFee
      ? [
          {
            type: 'selectedPlan',
            product: selectedPlan?.planName,
            price: `${selectedPlan?.amount}`,
            billingPreference: billingPreference?.value,
            planCode: selectedPlan?.planCode,
          },
          {
            type: 'selectedPlan',
            product: 'Onboarding Fee',
            price: `$${parseFloat(selectedPlan?.onboarding || 0).toFixed(2)}`,
            planCode: 'onboarding-fee',
            id: 'onboarding-fee',
          },
        ]
      : [
          {
            type: 'selectedPlan',
            product: selectedPlan?.planName,
            price: `${parseFloat(selectedPlan?.amount || 0).toFixed(2)}`,
            billingPreference: billingPreference?.value,
            planCode: selectedPlan?.planCode,
          },
        ],
  )
  console.log('listOfProducts - on init', listOfProducts)
  const [listOfAddons, setListOfAddons] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [gmailPackageConfiguration, setGmailPackageConfiguration] = useState({
    totalSeats: 0,
    availableSeats: 0,
  })
  const [isSEOExternalDomain, setIsSEOExternalDomain] = useState(null)
  const [SEOAlphaDomain, setSEOAlphaDomain] = useState(null)

  //const [isLegacyUpgradeAllowed, setIsLegacyUpgradeAllowed] = useState(false)

  const isPlusPackage = listOfProducts.some(
    x => x.product === 'Plus (6 mo. Term)' || x.product === 'Plus',
  )
  const isProfessionalPackage = listOfProducts.some(
    x =>
      x.product === 'Professional (6 mo. Term)' ||
      x.product === 'Professional' ||
      x.product === 'Pro (12 mo. Term)',
  )
  const isUnlimitedPackage = listOfProducts.some(
    x => x.product === 'Unlimited (6 mo. Term)' || x.product === 'Unlimited',
  )
  const isUnlimitedEnterprisePackage = listOfProducts.some(
    x => x.product === 'Unlimited Enterprise',
  )
  const isSync = listOfProducts.some(
    x => x.product === 'Sync (6 mo. Term)' || x.product === 'Sync',
  )
  const isThryvLeads = listOfProducts.some(x => x.product === 'Thryv Leads')
  const isMarketingCenter = listOfProducts.some(x =>
    ADD_ON.add_ons['Marketing Center Plus'].addOnCodes.includes(x.planCode),
  )
  const hasMC = accountInfo?.addOns?.find(
    addon =>
      ADD_ON.add_ons['Marketing Center Plus'].addOnCodes.includes(
        addon.addOnCode,
      ) ||
      ADD_ON.add_ons['Marketing Center Pro'].addOnCodes.includes(
        addon.addOnCode,
      ),
  )
  const hasSEO = accountInfo?.addOns?.find(addon => addon.name.includes('SEO'))
  const [mcDomainConfig, setMcDomainConfig] = useState({
    isValid: hasMC ? true : false,
    value: '',
    shouldValidate: false,
    hideDomain: hasMC ? true : false,
  })
  const [seoDomainConfig, setSeoDomainConfig] = useState({
    isValid: hasSEO ? true : false,
    value: '',
    shouldValidate: false,
    SEOSelected: false,
    hideDomain: hasSEO ? true : false,
  })
  const [shouldIncludeCSNAddon, setShouldIncludeCSNAddon] = useState(false)
  const [cSNAddonInfo, setCSNAddonInfo] = useState()
  const [orderTotals, setOrderTotals] = useState()
  const client = useAuthClient()
  const {run} = useAsync({
    status: 'pending',
  })
  const [searchParams] = useSearchParams()

  const showaddons = searchParams.get('showaddons')
  const SHOW_HIPAA_ADDON =
    (selectedCountry === 'usa' &&
      HIPAA_COMPLIANT_INDUSTRIES.includes(selectedIndustry)) ||
    accountInfo?.addOns?.some(({addOnCode}) =>
      ['addon-4-hipaa-m2m', 'addon-4-hipaa-semi'].includes(addOnCode),
    )

  const SHOW_SEO_MBG_ADDON =
    listOfProducts.find(product => WEBSITE_ADDON_CODES.includes(product.id)) ||
    accountInfo.isLegacySEO

  const isSEOInCart = listOfProducts.find(
    product =>
      product.product === 'SEO Non-Guaranteed' ||
      product.product === 'SEO Money Back Guarantee',
  )

  const websiteEntWithMCProPlans = NO_WEBSITE_ENT_PLANS.includes(
    selectedPlan?.planCode,
  )
  const hasCSN = accountInfo.addOns.some(
    x => x.name === ADD_ON.add_ons['Thryv CSN'].label || x.name === 'Thryv ESS',
  )
  const upgradeToUnlimited = selectedPlan?.planName.includes('Unlimited')
  const isSupportFeeIncluded = accountInfo.addOns.some(
    ({addOnCode}) => addOnCode === '247support-m2m',
  )
  const isSyncPlan = selectedPlan?.planCode?.includes('sync')
  const isMCIncluded = accountInfo.addOns.some(({addOnCode}) =>
    MC_PLAN_CODES.includes(addOnCode),
  )
  const isMCProEnhancedInCart = listOfProducts.some(({planCode}) =>
    ADD_ON.add_ons['Thryv Signature + Expanded Listings'].addOnCodes.includes(
      planCode,
    ),
  )

  // =================================================
  // temp fix logic provided
  let currentAddons = [...accountInfo.addOns, ...accountInfo.legacyAddOns]
  let isTLMCAvailable = currentAddons.find(item =>
    item?.addOnCode.includes('tl-'),
  )
  // ====================================================

  const filterCSN = React.useCallback(
    (listOfAddons, name) => {
      return hasCSN
        ? listOfAddons
        : listOfAddons.filter(({name: addonName}) => addonName !== name)
    },
    [hasCSN],
  )

  const data = React.useMemo(() => {
    const filteredAddonsByFlags =
      listOfAddons?.addOns?.filter(({requiresFlags}) =>
        isRequiredFlagEnabled(userFeatures, requiresFlags),
      ) || []
    const isMcInListOfProducts = listOfProducts.some(({planCode}) =>
      MC_PLAN_CODES.includes(planCode),
    )
    const isBc4InListOfProducts = listOfProducts.some(({planCode}) =>
      BC_4_PLAN_CODES.includes(planCode),
    )
    const isBc6Standalone =
      listOfProducts.some(({planCode}) => BC_6_PLAN_CODES.includes(planCode)) &&
      !isMcInListOfProducts
    const isBCSyncStandalone =
      listOfProducts.some(({planCode}) =>
        BC_SYNC_PLAN_CODES.includes(planCode),
      ) && !isMcInListOfProducts
    const filteredAddons = filterCSN(
      filteredAddonsByFlags,
      ADD_ON.add_ons['Thryv CSN'].label,
    ).filter(addon => {
      const addonBilling = addon[billingPreference.value]
      const code = addonBilling?.[0]?.code

      if (
        code === STRATEGIC_CONTENT_CREATION_CODE &&
        !['US', 'CA', 'AU', 'NZ'].includes(
          selectedCountryAbbreviation?.toUpperCase(),
        )
      ) {
        return false
      }
      if (
        code === STRATEGIC_CONTENT_CREATION_CODE &&
        !isMcInListOfProducts &&
        !isBc4InListOfProducts
      ) {
        return false
      }
      if (
        code === STRATEGIC_CONTENT_CREATION_CODE &&
        isBCSyncStandalone &&
        ['CA', 'AU', 'NZ'].includes(selectedCountryAbbreviation?.toUpperCase())
      ) {
        return false
      }

      if (
        LISTING_REFRESH_CODES.includes(code) &&
        (!userFeatures.Enable_Listing_Refresh || isBc6Standalone)
      ) {
        return false
      }

      if (
        LISTING_REFRESH_CODES.includes(code) &&
        !['US'].includes(selectedCountryAbbreviation?.toUpperCase())
      ) {
        return false
      }
      return addonBilling
    })

    if (filteredAddonsByFlags) {
      if (SHOW_HIPAA_ADDON && SHOW_SEO_MBG_ADDON) {
        return filteredAddons
      } else if (SHOW_HIPAA_ADDON && !SHOW_SEO_MBG_ADDON) {
        return filteredAddons.filter(
          ({name}) => name !== ADD_ON.add_ons['SEO Money Back Guarantee'].label,
        )
      } else if (SHOW_SEO_MBG_ADDON) {
        return filteredAddons.filter(
          ({name}) => name !== ADD_ON.add_ons['HIPAA'].label,
        )
      }
      return filteredAddons.filter(
        ({name}) =>
          name !== ADD_ON.add_ons['SEO Money Back Guarantee'].label &&
          name !== ADD_ON.add_ons['HIPAA'].label,
      )
    }
  }, [
    listOfProducts,
    listOfAddons?.addOns,
    userFeatures,
    filterCSN,
    billingPreference.value,
    SHOW_HIPAA_ADDON,
    SHOW_SEO_MBG_ADDON,
    selectedCountryAbbreviation,
  ])

  const updateAvailableGmailSeats = value => {
    const {availableSeats} = gmailPackageConfiguration
    if (availableSeats >= 0) {
      if (availableSeats - value < 0) {
        return
      }
      setGmailPackageConfiguration(prev => ({
        ...prev,
        availableSeats: prev.availableSeats - value,
      }))
    }
  }

  const setSelectedCSNAddonInfo = () => {
    if (cSNAddonInfo) return
    const cSNAddon = listOfProducts.find(
      ({product}) =>
        product === ADD_ON.add_ons['Thryv CSN'].label ||
        product === 'Thryv ESS',
    )
    if (cSNAddon) {
      setCSNAddonInfo([
        {
          ...cSNAddon,
          price: cSNAddon.price.replace(/[$,a-zA-Z]/g, ''),
        },
      ])
    }
  }

  const addProduct = productList => {
    //=== tl mc changes added
    console.log({productList})
    if (productList.length) {
      const disableApps = getDisableApps(productList)
      const appsToDisable = listOfProducts.filter(item =>
        disableApps.includes(item.planCode),
      )
      if (appsToDisable.length) {
        removeProduct(appsToDisable.map(item => item.id))
      }
      const isNewTLIncluded = productList.find(item =>
        item?.planCode.includes('tl-mc'),
      )
      if (isNewTLIncluded) {
        if (isNewTLMCAccessible) {
          const isGrowthPackageInclude = listOfProducts.find(item =>
            item.product.includes('Growth Package'),
          )
          if (isGrowthPackageInclude) {
            removeProduct([isGrowthPackageInclude?.id])
          }
        } else {
          const isMCIncluded = accountInfo?.addOns?.some(({addOnCode}) =>
            MC_PLAN_CODES.includes(addOnCode),
          )
          const isMCInCart = listOfProducts?.some(({planCode}) =>
            MC_PLAN_CODES.includes(planCode),
          )
          const isTLIncluded = accountInfo?.addOns?.some(({addOnCode}) =>
            TL_CODES.includes(addOnCode),
          )
          if (!isMCInCart && !isMCIncluded) {
            if (!isTLIncluded) {
              productList = productList.map(item => {
                if (item?.planCode.includes('tl-mc')) {
                  item.planCode = item?.planCode.replace('tl-mc', 'tl')
                }
                return item
              })
            }
          }
        }
      }

      const isNewSocialAds = productList.find(item =>
        item?.product.includes('Social Ads'),
      )

      if (isNewSocialAds || isNewTLIncluded) {
        const noPaidMcInCart = !listOfProducts.some(
          item =>
            item?.product === 'Marketing Center Plus' ||
            item?.product === 'Marketing Center Pro',
        )

        if (noPaidMcInCart && selectedCountry === 'US') {
          const mcFoundationAddon = listOfAddons?.addOns?.find(
            item => item?.name === 'Marketing Center Foundation',
          )
          productList = [
            ...productList,
            getProduct({
              item: mcFoundationAddon,
              currency: CURRENCYS[selectedCountry],
              quantity: 1,
              selectedPlan:
                isNewSocialAds?.billingPreference ||
                isNewTLIncluded?.billingPreference,
              currentAddOns: currentAddons,
            }),
          ]
        }
      }

      const addingThryvSuccessServices = productList.find(item =>
        THRYV_SUCCESS_SERVICES_CODES.includes(item?.planCode),
      )

      const existingThryvSuccessServices = listOfProducts.find(item =>
        THRYV_SUCCESS_SERVICES_CODES.includes(item?.planCode),
      )

      if (addingThryvSuccessServices && existingThryvSuccessServices) {
        return setListOfProducts(prev => {
          return removeDuplicatesFromListOfProducts(
            [...prev, ...productList].filter(
              item => item.id !== existingThryvSuccessServices.id,
            ),
          )
        })
      }
    }
    // ===
    setListOfProducts(prev =>
      removeDuplicatesFromListOfProducts([...prev, ...productList]),
    )
  }

  const updateProduct = (updatedProduct, compareFieldName) => {
    const callbackFn = prev => {
      return removeDuplicatesFromListOfProducts(
        prev.map(product => {
          if (compareFieldName) {
            if (
              product.type === updatedProduct.type &&
              product[compareFieldName] === updatedProduct[compareFieldName]
            ) {
              return {...product, ...updatedProduct}
            }
          }
          if (product.id === updatedProduct.id) {
            return {...product, ...updatedProduct}
          } else if (
            product?.planCode?.includes('4-seobase-') &&
            updatedProduct?.planCode?.includes('4-seobase-')
          ) {
            return {
              ...product,
              proposalId: updatedProduct.proposalId,
              domain: updatedProduct.domain,
              price: updatedProduct.price,
              quantity: updatedProduct.quantity,
              seoBillingDate: updatedProduct.seoBillingDate,
            }
          }

          return product
        }),
      )
    }
    setListOfProducts(callbackFn)
  }

  const removeProduct = (productsId, customFilter) => {
    const isMCProduct = listOfProducts.find(
      item =>
        item.product.includes('Marketing Center') && item.id === productsId[0],
    )
    const isMCIncluded = accountInfo.addOns.find(({addOnCode}) =>
      MC_PLAN_CODES.includes(addOnCode),
    )
    const isMCFoundationIncluded = accountInfo.addOns.find(({addOnCode}) =>
      'addon-4-mcfoundation-m2m'.includes(addOnCode),
    )
    let newSEOIds = []
    let mcDependentIds = []
    if (isMCProduct) {
      const mcProductIndex = listOfProducts.findIndex(
        item => item.id === productsId[0],
      )
      if (mcProductIndex !== -1 && !isMCIncluded && !isMCFoundationIncluded) {
        for (let i = mcProductIndex + 1; i < listOfProducts.length; i++) {
          if (
            !accountInfo?.addOns?.some(({addOnCode}) =>
              listOfProducts[i]?.planCode.includes(addOnCode),
            )
          ) {
            mcDependentIds.push(listOfProducts[i].id)
          }
        }
        if (mcDependentIds.length > 0) {
          setShowRefreshModal(true)
        }
      }
      listOfProducts.map(item => {
        if (
          [
            'addon-4-seokey-m2m',
            'addon-4-seopb-m2m',
            'addon-4-seobase-m2m',
          ].includes(item.planCode)
        ) {
          newSEOIds.push(item.id)
        }
        return item
      })
    }
    if (!Array.isArray(productsId)) productsId = [productsId]
    if (newSEOIds.length > 0) {
      productsId = [...productsId, ...newSEOIds]
    }
    if (mcDependentIds.length > 0) {
      productsId = [...productsId, ...mcDependentIds]
    }
    const productsToRemove = listOfProducts.filter(item =>
      productsId.includes(item.id),
    )
    const disableApps = getDisableApps(productsToRemove)
    const appsToReactive = data
      .filter(item => {
        const defaultItem = currentAddons.find(
          ({addOnCode}) => addOnCode === getItemCode(item, addOnCode),
        )
        if (!defaultItem) return false
        const isRemovingThryvSuccessServices = productsToRemove.find(
          ({planCode}) => THRYV_SUCCESS_SERVICES_CODES.includes(planCode),
        )
        const isDefaultItemThryvSuccessService =
          THRYV_SUCCESS_SERVICES_CODES.includes(defaultItem.addOnCode)
        if (
          isRemovingThryvSuccessServices &&
          isDefaultItemThryvSuccessService
        ) {
          return true
        }
        return disableApps.includes(defaultItem.addOnCode)
      })
      .map(item => {
        return getProduct({
          item,
          selectedPlan,
          currency: CURRENCYS[selectedCountry],
          quantity: 1,
          currentAddOns: currentAddons,
        })
      })

    let filter
    if (customFilter) {
      filter = customFilter
    } else {
      filter = ({id}) => !productsId.includes(id)
    }

    setListOfProducts(prev => {
      let updatedProducts = [...prev.filter(filter), ...appsToReactive]

      const isMcFoundationNew =
        updatedProducts.find(
          item => item.product === 'Marketing Center Foundation',
        ) &&
        !accountInfo.addOns.some(({addOnCode}) =>
          addOnCode.includes('addon-4-mcfoundation-m2m'),
        )

      if (isMcFoundationNew) {
        const shouldMcFoundationRemain = updatedProducts.some(
          item =>
            item.product.includes('Social Ads') ||
            item.product.includes('Thryv Leads'),
        )
        if (!shouldMcFoundationRemain) {
          updatedProducts = updatedProducts.filter(
            item => item.product !== 'Marketing Center Foundation',
          )
        }
      }
      return removeDuplicatesFromListOfProducts(updatedProducts)
    })
  }

  const debouncedOrderTotals = useDebounce(orderTotals, 500)

  useEffect(() => {
    const isMCIncluded = accountInfo.addOns.some(({addOnCode}) =>
      MC_PLAN_CODES.includes(addOnCode),
    )
    const supportFeeInCart = listOfProducts.find(
      ({planCode}) => planCode === '247support-m2m',
    )
    const subtotal = +orderTotals
      ?.find(({product}) => product === 'Subtotal')
      ?.price?.replace('$', '')
      .replace(',', '')

    if (isTSS && (isMCIncluded || !isSyncPlan) && !isSupportFeeIncluded) {
      if (supportFeeInCart && accountInfo.subtotal >= subtotal - 9) {
        removeProduct([supportFeeInCart.id])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOrderTotals])

  useEffect(() => {
    if (!isSyncPlan) return

    const isMCInCart = listOfProducts.some(({planCode}) =>
      MC_PLAN_CODES.includes(planCode),
    )
    const isOnboardingFeeInCart = listOfProducts.some(
      ({planCode}) => planCode === 'onboarding-fee',
    )
    const onboardingFee = {
      type: 'selectedPlan',
      product: 'Onboarding Fee',
      price: `$${parseFloat(selectedPlan.onboarding).toFixed(2)}`,
      planCode: 'onboarding-fee',
      id: 'onboarding-fee',
    }

    if (
      isMCInCart &&
      !isMCIncluded &&
      !accountInfo.hasPaidOnboardingFee &&
      !isOnboardingFeeInCart
    ) {
      addProduct([onboardingFee])
    }

    if (!isMCInCart && !isMCIncluded && isOnboardingFeeInCart) {
      removeProduct([onboardingFee.id])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOrderTotals])

  const validateRequiredAddons = async () => {
    if (
      selectedPlan.planName === 'Thryv Leads' &&
      !listOfProducts.some(
        product =>
          product.product === 'Thryv Leads' && product.type === 'addon',
      )
    ) {
      toast.error('This plan requires Thryv Leads add-on.')
      return
    }
    let is24x7FeeRequired = false
    let filteredListOfProducts = [...listOfProducts]
    const isNewSEOIncluded = filteredListOfProducts.some(
      ({planCode: itemCode}) => itemCode === 'addon-4-seobase-m2m',
    )
    const isMCInCart = listOfProducts?.some(({planCode}) =>
      MC_PLAN_CODES.includes(planCode),
    )
    const isMCFoundationInCart = listOfProducts?.some(({planCode}) =>
      'addon-4-mcfoundation-m2m'.includes(planCode),
    )
    const isNewTLIncluded = filteredListOfProducts.some(
      ({planCode: itemCode}) =>
        itemCode === 'addon-4-tl-tax-m2m' || itemCode === 'addon-4-tl-mc-m2m',
    )
    const isNewSocialAdsIncluded = filteredListOfProducts.some(
      ({planCode: itemCode}) =>
        [
          'addon-4-socialadsbronze-m2m',
          'addon-4-socialadsgold-m2m',
          'addon-4-socialadssilver-m2m',
        ].includes(itemCode),
    )
    const BC_PLANS = ['Unlimited', 'Professional', 'Plus', 'Sync']
    const isBCInDefault = BC_PLANS.some(plan =>
      accountInfo?.planName?.toLowerCase().startsWith(plan.toLowerCase()),
    )
    const supportBillingPref = 'monthly'
    const winBackCoupon = WIN_BACK_4_COUPONS[selectedPlan?.planCode]
    const isSupportFeeInCart = listOfProducts.some(
      ({planCode}) => planCode === '247support-m2m',
    )
    if (
      isUpgrade &&
      accountInfo.status !== 'active' &&
      winBackCoupon !== undefined
    ) {
      is24x7FeeRequired = false
    } else if (isSyncPlan) {
      is24x7FeeRequired = false
    } else {
      if (isSupportFeeInCart) {
        is24x7FeeRequired = true
      }
    }

    if (accountInfo.planCode !== selectedPlan?.planCode) {
      is24x7FeeRequired = false
    }
    if (is24x7FeeRequired) {
      if (!isSupportFeeInCart) {
        const {centers} = all5PlanPrices
        const supportAndServiceFee = centers.find(
          ({code}) => code === '247support-m2m',
        )
        const {unitAmount} = supportAndServiceFee.currencies.find(
          ({currency}) => currency === CURRENCYS[selectedCountry],
        )
        const supportFee = {
          id: supportAndServiceFee.id,
          billingPreference: supportBillingPref,
          planCode: supportAndServiceFee.code,
          price: `$${unitAmount.toFixed(2).toString()}`,
          product: supportAndServiceFee.name,
          quantity: 1,
          type: ADDONS_TYPES['ADD_ON'],
        }
        filteredListOfProducts.push(supportFee)
      }
    } else {
      filteredListOfProducts = listOfProducts.filter(
        product => product.planCode !== '247support-m2m',
      )
    }
    const mcFoundation = {
      id: 'marketingFoundation',
      billingPreference: supportBillingPref,
      planCode: 'addon-4-mcfoundation-m2m',
      price: `$0`,
      product: 'Marketing Center Foundation',
      quantity: 1,
      type: ADDONS_TYPES['ADD_ON'],
    }

    const shouldAddMarketingFoundation =
      (isNewSEOIncluded || isNewTLIncluded || isNewSocialAdsIncluded) &&
      !isMCInCart &&
      !isMCFoundationInCart &&
      isBCInDefault &&
      selectedCountryAbbreviation === 'US' &&
      !filteredListOfProducts?.some(({planCode}) =>
        'addon-4-mcfoundation-m2m'.includes(planCode),
      )

    if (shouldAddMarketingFoundation) {
      filteredListOfProducts.push(mcFoundation)
    }

    console.log('filteredListOfProducts', filteredListOfProducts)

    const selectedLines = getOrderLines4(listOfProducts)
    captureOrder4NewUpgrade(
      'addons-selection-continue',
      'upgrade',
      selectedLines,
    )
    nextStep({
      selectedAddons: filteredListOfProducts,
    })
  }

  const getOrderLines4 = listOfProducts => {
    console.log('listOfProducts', listOfProducts)
    console.log(
      'orderData4CU.accountInfo.addOns',
      orderData4CU.accountInfo.addOns,
    )
    const updatedAllLines = getAllSelectedDefaultLines(
      listOfProducts,
      orderData4CU.accountInfo.addOns,
    )
    let selectedLines = []
    let line_counter = 1

    for (let product of updatedAllLines) {
      let unitAmount = 0
      if (product.price) {
        if (product.price.indexOf('$') >= 0) {
          unitAmount = Number(product.price.split('$')[1].replaceAll(',', ''))
        } else {
          unitAmount = product.price
        }
      }

      if (product.planCode === 'seobase-5-thryv-m2m') {
        console.log('SEO DOLOR DE', product)
      }

      const pUAmount =
        product.unitAmount || product.unitAmount === 0
          ? product.unitAmount
          : unitAmount || unitAmount === 0
          ? unitAmount
          : null
      let pUPrice = pUAmount
      if (pUAmount) {
        if (
          product.planCode === 'addon-4-seobase-m2m' ||
          product.planCode === 'addon-4-seokey-m2m' ||
          product.planCode === 'addon-4-seopb-m2m'
        ) {
          pUPrice = pUAmount * 1
        } else if (
          product.planCode === 'seobase-5-thryv-m2m' ||
          product.addOnCode === 'seobase-5-thryv-m2m'
        ) {
          pUPrice = pUAmount * (product.amount || product.quantity || 1)
        } else {
          pUPrice = pUAmount * (product.quantity ? product.quantity : 1)
        }
      }
      // let oneTimeAmount = null
      // if (product.type === 'oneTime') {
      //   let oUPrice =
      //     product.unitAmount || product.unitAmount === 0
      //       ? product.unitAmount
      //       : unitAmount || unitAmount === 0
      //       ? unitAmount
      //       : null
      //   if (oUPrice) {
      //     oUPrice = oUPrice * (product.quantity ? product.quantity : 1)
      //   }
      //   oneTimeAmount = oUPrice
      // }
      selectedLines.push({
        line_number: line_counter,
        parent_line_number: null,
        plansv5_id: product.id ? product.id : '',
        action_code: product.tempLineActionCode,
        order_line_status: 'InProgress',
        saas_product_category:
          product.type === 'entitlements'
            ? 'Center'
            : product.type === 'selectedPlan'
            ? 'Center'
            : product.type === 'oneTime'
            ? 'OneTime'
            : 'App',
        saas_product_type: product.type ? product.type : 'addon',
        // saas_product_type:
        //   product.type === 'selectedPlan'
        //     ? 'main'
        //     : product.type
        //     ? product.type
        //     : 'addon',
        saas_product_code: (() => {
          const code = product.planCode || product.addOnCode
          if (
            code &&
            code.startsWith('addon-4-tl') &&
            product.tempLineActionCode === 'New' &&
            userFeatures?.TL_Tax_Plan_Enabled
          ) {
            return 'addon-4-tl-tax-m2m'
          }
          return code
        })(),
        saas_instance_id: null,
        recurly_subscription_id: product.uuid,
        total_cost: pUPrice,
        unit_cost: pUAmount,
        // saas_product_price: pUPrice,
        // saas_product_unit_amount: pUAmount,
        quantity:
          product.planCode === 'seobase-5-thryv-m2m' ||
          product.addOnCode === 'seobase-5-thryv-m2m'
            ? product.amount || product.quantity || 1
            : product.quantity
            ? product.quantity
            : 1,
        // one_time_fee: oneTimeAmount,
        // one_time_fee: null,
        start_date: new Date(),
        end_date: null,
      })
      line_counter = line_counter + 1
    }
    console.log('selectedLines ', selectedLines)
    return selectedLines
  }

  const getAllSelectedDefaultLines = (lines1, lines2) => {
    const sLines = [...lines1]
    const dLines = [...lines2]
    // const tempOrderLines = []
    const selectedAndDefaultlines = []
    // const defaultLines = []

    dLines.forEach(dLine => {
      let found = false
      sLines.forEach(sLine => {
        if (dLine.addOnCode === sLine.planCode) {
          dLine.tempLineActionCode = 'Update'
          sLine.tempLineActionCode = 'Update'
          dLine.type = sLine.type
          found = true
        }
        // else {
        //   if (sLine.type === 'selectedPlan') {
        //     sLine.tempLineActionCode = 'Update'
        //     selectedAndDefaultlines.push(sLine)
        //   }
        // }
      })
      if (!found) {
        // if (dLine.type === 'selectedPlan') {
        //   dLine.tempLineActionCode = 'Update'
        // } else {
        dLine.tempLineActionCode = 'Cancel'
        // }
      }
      selectedAndDefaultlines.push(dLine)
    })

    // sLines.forEach(sLine => {
    //   let found = false
    //   dLines.forEach(dLine => {
    //     if (sLine.planCode === dLine.addOnCode) {
    //       sLine.tempLineActionCode = 'Update'
    //       found = true
    //       selectedAndDefaultlines.push(dLine)
    //     }
    //   })
    //   if (!found) {
    //     if (sLine.type === 'selectedPlan') {
    //       sLine.tempLineActionCode = 'Update'
    //     } else {
    //       sLine.tempLineActionCode = 'New'
    //     }
    //     selectedAndDefaultlines.push(sLine)
    //   }
    // })

    sLines.forEach(sLine => {
      const foundInArray2 = dLines.find(
        dLine => sLine.planCode === dLine.addOnCode,
      )
      // sLine.tempLineActionCode = foundInArray2 ? 'Update' : 'New'
      // if (!foundInArray2) selectedAndDefaultlines.push(sLine)
      if (!foundInArray2) {
        if (sLine.type === 'selectedPlan') {
          sLine.tempLineActionCode = 'Update'
        } else {
          sLine.tempLineActionCode = 'New'
        }
        selectedAndDefaultlines.push(sLine)
      }
    })

    console.log('selectedAndDefaultlines - ', selectedAndDefaultlines)
    // console.log('defaultLines - ', defaultLines)

    // tempOrderLines.push(...selectedlines, ...defaultLines)
    // console.log('tempOrderLines - ', tempOrderLines)

    return selectedAndDefaultlines
  }

  const getGmailSeats = () => {
    if (isPlusPackage) {
      return {
        availableSeats: 2,
        totalSeats: 2,
      }
    }
    if (isProfessionalPackage) {
      return {
        availableSeats: 5,
        totalSeats: 5,
      }
    }
    if (isSync) {
      return {
        availableSeats: 1,
        totalSeats: 1,
      }
    }
    if (isUnlimitedPackage || isUnlimitedEnterprisePackage) {
      return {
        availableSeats: 10,
        totalSeats: 10,
      }
    }
    if (isThryvLeads || isMarketingCenter) {
      return {
        availableSeats: 0,
        totalSeats: 0,
      }
    }
  }
  useEffect(() => {
    setGmailPackageConfiguration(getGmailSeats())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan])
  useEffect(() => {
    if (shouldIncludeCSNAddon) {
      if (
        listOfProducts.some(
          ({product}) =>
            product === ADD_ON.add_ons['Thryv CSN'].label ||
            product === 'Thryv ESS',
        )
      )
        return
      addProduct(cSNAddonInfo)
      setShouldIncludeCSNAddon(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldIncludeCSNAddon, cSNAddonInfo, listOfProducts])
  useEffect(() => {
    const getAddonsPrices = async () => {
      let url
      if (showaddons) {
        url = `plans/list/all`
      } else {
        // Mirrors isMCIncluded/isMCFoundationIncluded in removeProduct() above.
        const hasEligibleMcOrFoundation = accountInfo.addOns.some(
          ({addOnCode}) =>
            MC_PLAN_CODES.includes(addOnCode) ||
            addOnCode === 'addon-4-mcfoundation-m2m',
        )
        url = `plans/list/addons/${selectedPlan.name}?hasEligibleMcOrFoundation=${hasEligibleMcOrFoundation}`
      }
      const response = await run(client(url))
      setListOfAddons(response)
      setIsLoading(false)
    }

    getAddonsPrices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (listOfProducts.length > 1) {
      setSelectedCSNAddonInfo()
      if (
        accountInfo?.addOns.some(
          x =>
            x.name ===
              ADD_ON.add_ons['Google My Business Optimization'].label ||
            x.name.includes('GMB'),
        ) &&
        !listOfProducts.some(
          x =>
            x.product ===
            ADD_ON.add_ons['Google My Business Optimization'].label,
        )
      ) {
        const {addOnCode} = accountInfo.addOns.some(
          x =>
            x.name ===
              ADD_ON.add_ons['Google My Business Optimization'].label ||
            x.name.includes('GMB'),
        )
        if (listOfAddons.addOns) {
          const gmbCurrency = listOfAddons.addOns
            .find(
              x =>
                x.name ===
                ADD_ON.add_ons['Google My Business Optimization'].label,
            )
            [billingPreference.value][0].currencies.find(
              x => x.currency === CURRENCYS[selectedCountry],
            )
          const gmbPrice = `$${gmbCurrency.unitAmount
            .toFixed(2)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
          const {id} = gmbCurrency
          const product = {
            id: id,
            billingPreference: billingPreference.value,
            planCode: addOnCode,
            price: gmbPrice,
            product: ADD_ON.add_ons['Google My Business Optimization'].label,
            quantity: 1,
            type: 'addon',
          }
          addProduct([product])
        }
      }
    }
  }, [listOfProducts]) //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mcDomainConfig.isValid) {
      let mcAddon = listOfProducts.find(
        mc =>
          ADD_ON.add_ons['Marketing Center Plus'].addOnCodes.includes(
            mc.planCode,
          ) ||
          ADD_ON.add_ons['Marketing Center Pro'].addOnCodes.includes(
            mc.planCode,
          ) ||
          ADD_ON.add_ons[
            'Thryv Signature + Expanded Listings'
          ].addOnCodes.includes(mc.planCode),
      )
      mcAddon = {...mcAddon, sensisSiteDomain: mcDomainConfig.value}
      updateProduct(mcAddon)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mcDomainConfig])
  useEffect(() => {
    if (seoDomainConfig.isValid) {
      let seoAddon = listOfProducts.find(x => x.product.includes('SEO'))
      seoAddon = {...seoAddon, seoDomain: seoDomainConfig.value}
      updateProduct(seoAddon)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seoDomainConfig])

  useEffect(() => {
    if (isSEOInCart && !hasSEO) {
      if (!(seoDomainConfig.value && seoDomainConfig.isValid)) {
        setSeoDomainConfig(prev => ({
          ...prev,
          isValid: false,
          shouldValidate: true,
          hideDomain: false,
        }))
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfProducts])

  useEffect(() => {
    async function addWebsiteEnt() {
      const response = await client(`plans/list/all`)
      const websiteEnt = response.entitlements.find(
        ({code}) => code === 'ent-4-website-free',
      )
      const newEntitlements = [...listOfAddons.entitlements]
      newEntitlements.push(websiteEnt)
      setListOfAddons(prev => ({...prev, entitlements: newEntitlements}))
    }
    const isMCProInCart = listOfProducts.some(({planCode}) =>
      ADD_ON.add_ons['Marketing Center Pro'].addOnCodes.includes(planCode),
    )
    const isWebsiteInEntArray = listOfAddons.entitlements?.some(
      ({code}) => code === 'ent-4-website-free',
    )

    if (
      (isMCProInCart || isMCProEnhancedInCart) &&
      websiteEntWithMCProPlans &&
      !isWebsiteInEntArray
    ) {
      addWebsiteEnt()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfProducts, selectedPlan?.planCode, websiteEntWithMCProPlans])
  useEffect(() => {
    if (listOfProducts.length === 0) return
    const websiteEnt = listOfProducts.find(
      ({planCode}) => planCode === 'ent-4-website-free',
    )
    const isMCProInCart = listOfProducts.some(
      ({planCode}) => planCode === 'addon-4-mcpro-m2m',
    )

    if (
      listOfAddons?.addOns &&
      websiteEntWithMCProPlans &&
      !isMCProInCart &&
      websiteEnt !== undefined &&
      !websiteEnt
    ) {
      removeProduct([websiteEnt?.id])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountInfo.addOns,
    listOfProducts,
    selectedPlan?.planCode,
    listOfAddons?.addOns,
    websiteEntWithMCProPlans,
  ])

  useEffect(() => {
    const professionalServices = accountInfo.addOns.find(
      ({addOnCode}) => addOnCode === 'proservices-m2m',
    )
    if (!professionalServices) return undefined
    const isInListOfProducts = listOfProducts.some(
      ({planCode}) => professionalServices.addOnCode === planCode,
    )
    if (isInListOfProducts) return undefined
    addProduct([
      {
        id: professionalServices.id,
        planCode: professionalServices.addOnCode,
        price: formatPrice(professionalServices.unitAmount),
        product: professionalServices.name,
        quantity: professionalServices.quantity,
        type: 'addon',
        billingPreference: 'monthly',
      },
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfProducts, accountInfo.addOns])

  const disableNext =
    (mcDomainConfig.shouldValidate && !mcDomainConfig.isValid) ||
    (seoDomainConfig.SEOSelected &&
      (!SEOAlphaDomain || isSEOExternalDomain === null))

  console.log('data=====>', {data})
  return (
    <NewOrderLayout
      prevStep={prevStep}
      title="Entitlements & Add-Ons"
      disableNextButton={disableNext}
      showContinueButton={true}
      showCancelButton={true}
      nextStep={validateRequiredAddons}
      cohortEaid
      setCohortEaid
      order5NUId={order5NUId}
      isCancelOrder5={isCancelOrder5}
      cancelOrder5={cancelOrder5}
      isBillingNoticeDisplayed={isBillingNoticeDisplayed}
      setIsBillingNoticeDisplayed={setIsBillingNoticeDisplayed}
    >
      <div>
        <div style={{paddingLeft: '5px'}}>
          <AccountInfo data={accountInfo} />
          {showRefreshModal && <McConfirmModal></McConfirmModal>}
          <hr style={{width: '85%'}} />
        </div>

        <CartAddons
          listOfAddons={listOfProducts}
          selectedCountry={selectedCountry}
          accountInfo={accountInfo}
          setOrderTotals={setOrderTotals}
          billingPreference={billingPreference}
        />
      </div>
      <div>
        {isLoading ? (
          <SkeletonLoader width="90%" height="50vh" />
        ) : (
          <>
            <Entitlements
              data={listOfAddons.entitlements}
              addProduct={addProduct}
              removeProduct={removeProduct}
              updateProduct={updateProduct}
              currency={CURRENCYS[selectedCountry]}
              listOfProducts={listOfProducts}
              currentAddOns={[
                ...accountInfo.addOns,
                ...accountInfo.legacyAddOns,
              ]}
              isWebsiteIncluded={isWebsiteIncluded}
              availableGmailSeats={gmailPackageConfiguration.availableSeats}
              updateAvailableGmailSeats={updateAvailableGmailSeats}
              totalGmailSeats={gmailPackageConfiguration.totalSeats}
              currentGmailItems={currentGmailItems}
              selectedPlan={selectedPlan}
              selectedCountry={selectedCountry}
              unlimitedUpgrade={upgradeToUnlimited}
              listOfAddons={listOfAddons}
              setListOfAddons={setListOfAddons}
              currentPlan={accountInfo.planCode}
              planCreationDate={accountInfo.createdAt}
            />
            <AddOns
              data={data}
              accountInfo={accountInfo}
              accountSubscriptionData={accountSubscriptionData}
              alphaDomain={SEOAlphaDomain}
              setAlphaDomain={setSEOAlphaDomain}
              setIsExternalDomain={setIsSEOExternalDomain}
              isExternalDomain={isSEOExternalDomain}
              addProduct={addProduct}
              removeProduct={removeProduct}
              updateProduct={updateProduct}
              selectedCountry={selectedCountry}
              currency={CURRENCYS[selectedCountry]}
              isUpgrade={true}
              industryCategoryType={industryCategoryType}
              listOfProducts={listOfProducts}
              currentAddOns={[
                ...accountInfo.addOns,
                ...accountInfo.legacyAddOns,
              ]}
              availableGmailSeats={gmailPackageConfiguration.availableSeats}
              updateAvailableGmailSeats={updateAvailableGmailSeats}
              totalGmailSeats={gmailPackageConfiguration.totalSeats}
              currentGmailItems={currentGmailItems}
              mcDomainConfig={mcDomainConfig}
              setMcDomainConfig={setMcDomainConfig}
              hasCSN={hasCSN}
              setShouldIncludeCSNAddon={setShouldIncludeCSNAddon}
              selectedPlan={selectedPlan}
              seoDomainConfig={seoDomainConfig}
              setSeoDomainConfig={setSeoDomainConfig}
              cohortEaid={cohortEaid}
              isEaidFound={isEaidFound}
              setAllowInvoice={setAllowInvoice}
              setCohortEaid={setCohortEaid}
              setIsEaidFound={setIsEaidFound}
              //setIsLegacyUpgradeAllowed={setIsLegacyUpgradeAllowed}
              thryvId={accountInfo?.thryvID}
              setIsCohort={setIsCohort}
              isLegacySEO={accountInfo.isLegacySEO}
              listOfAddons={listOfAddons}
              isNewTLMCAccessible={isNewTLMCAccessible}
              isThryvLeadsAvailable={isTLMCAvailable}
            />
            <OneTime
              data={listOfAddons.oneTimeAddOns}
              addProduct={addProduct}
              removeProduct={removeProduct}
              updateProduct={updateProduct}
              currency={CURRENCYS[selectedCountry]}
              listOfProducts={listOfProducts}
              currentAddOns={[
                ...accountInfo.addOns,
                ...accountInfo.legacyAddOns,
              ]}
              selectedCountry={selectedCountry}
              isNewTLMCAccessible={isNewTLMCAccessible}
            />
          </>
        )}
      </div>
    </NewOrderLayout>
  )
}

export {UpgradeAddonsSelection}
